import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * PageTransition — wraps a routed page so it animates elegantly in and out
 * when the user navigates between Academy pages (track → course → task → …).
 *
 * Style goal: subtle, "Apple-grade" motion. A soft fade combined with a tiny
 * vertical glide and an almost-imperceptible scale settle. Nothing flashy —
 * the kind of movement you feel more than you see.
 *
 * Used as the single keyed child of <AnimatePresence mode="wait">, so the
 * outgoing page finishes leaving before the next one arrives.
 */

// Apple-flavoured easing: a smooth, confident deceleration on enter and a
// crisp acceleration on exit.
const EASE_OUT = [0.16, 1, 0.3, 1];
const EASE_IN = [0.4, 0, 1, 1];

// How far pages travel on a sideways ("forward") transition, e.g. moving from
// a finished exercise to the next step.
const SLIDE_X = 56;

// Dynamic variants: receive the navigation direction as `custom` so both the
// entering page and the (already-detached) exiting page animate cohesively.
//   'forward' → horizontal slide (next exercise glides in from the right)
//   default   → the gentle vertical settle used for normal navigation
const variants = {
  initial: (dir) =>
    dir === 'forward'
      ? { opacity: 0, x: SLIDE_X, filter: 'blur(6px)' }
      : { opacity: 0, y: 12, scale: 0.992, filter: 'blur(6px)' },
  enter: (dir) => ({
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: dir === 'forward' ? 0.5 : 0.55, ease: EASE_OUT },
  }),
  exit: (dir) =>
    dir === 'forward'
      ? { opacity: 0, x: -SLIDE_X, filter: 'blur(4px)', transition: { duration: 0.3, ease: EASE_IN } }
      : { opacity: 0, y: -8, scale: 0.994, filter: 'blur(4px)', transition: { duration: 0.28, ease: EASE_IN } },
};

// Reduced-motion: keep it accessible with a plain, gentle cross-fade.
const reducedVariants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: 0.25, ease: 'linear' } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'linear' } },
};

const PageTransition = ({ children, direction = 'default' }) => {
  const prefersReduced = useReducedMotion();
  return (
    <motion.div
      className="ac-page-transition"
      custom={direction}
      variants={prefersReduced ? reducedVariants : variants}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
